import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpClient } from '../apiClient';
import type {
  ActivityLogDto,
  DashboardChartsDto,
  DashboardMetricsDto,
  InventoryInsightDto,
  RecentLedgerEntryDto,
  RecentSellAdjustmentDto,
  RecentSellInvoiceDto,
  RecentStockMovementDto,
  SalesUptimeChartDto,
} from '../types/dashboard.types';

const API_BASE = '/api/dashboard';

const shopDashboardKey = (shopId: string) => ['dashboard', shopId] as const;

const buildDateParams = (dateRange: string, startDate?: Date, endDate?: Date) => {
  const params = new URLSearchParams({ dateRange: dateRange || 'today' });
  if (startDate) params.set('startDate', startDate.toISOString());
  if (endDate) params.set('endDate', endDate.toISOString());
  return params;
};

export const useInvalidateShopDashboard = () => {
  const queryClient = useQueryClient();
  return (shopId: string) =>
    queryClient.invalidateQueries({ queryKey: shopDashboardKey(shopId) });
};

export const useShopDashboardMetrics = (
  shopId: string,
  dateRange: string,
  startDate?: Date,
  endDate?: Date,
  enabled = true,
) =>
  useQuery({
    queryKey: [
      ...shopDashboardKey(shopId),
      'metrics',
      dateRange,
      startDate?.toISOString(),
      endDate?.toISOString(),
    ],
    enabled: !!shopId && enabled,
    queryFn: async () => {
      const params = buildDateParams(dateRange, startDate, endDate);
      const response = await HttpClient.get<{ data: DashboardMetricsDto }>(
        `${API_BASE}/${shopId}/metrics?${params}`,
      );
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

export const useShopDashboardCharts = (
  shopId: string,
  dateRange: string,
  startDate?: Date,
  endDate?: Date,
  enabled = true,
) =>
  useQuery({
    queryKey: [
      ...shopDashboardKey(shopId),
      'charts',
      dateRange,
      startDate?.toISOString(),
      endDate?.toISOString(),
    ],
    enabled: !!shopId && enabled,
    queryFn: async () => {
      const params = buildDateParams(dateRange, startDate, endDate);
      const response = await HttpClient.get<{
        data: { revenueChart: DashboardChartsDto['revenueChart']; topItems: DashboardChartsDto['topItems'] };
      }>(`${API_BASE}/${shopId}/charts?${params}`);
      return {
        revenueChart: response.data?.revenueChart ?? { series: [] },
        topItems: response.data?.topItems ?? { items: [] },
      } satisfies DashboardChartsDto;
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

export const useShopDashboardSalesUptime = (
  shopId: string,
  dateRange: string,
  startDate?: Date,
  endDate?: Date,
  enabled = true,
) =>
  useQuery({
    queryKey: [
      ...shopDashboardKey(shopId),
      'sales-uptime',
      dateRange,
      startDate?.toISOString(),
      endDate?.toISOString(),
    ],
    enabled: !!shopId && enabled,
    queryFn: async () => {
      const params = buildDateParams(dateRange, startDate, endDate);
      const response = await HttpClient.get<{ data: SalesUptimeChartDto }>(
        `${API_BASE}/${shopId}/sales-uptime?${params}`,
      );
      return response.data ?? { bucket: 'day', series: [] };
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

export const useShopDashboardInventory = (shopId: string, enabled = true) =>
  useQuery({
    queryKey: [...shopDashboardKey(shopId), 'inventory'],
    enabled: !!shopId && enabled,
    queryFn: async () => {
      const response = await HttpClient.get<{ data: InventoryInsightDto[] }>(
        `${API_BASE}/${shopId}/inventory-insights`,
      );
      return response.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

export const useShopDashboardActivities = (shopId: string, limit = 15, enabled = true) =>
  useQuery({
    queryKey: [...shopDashboardKey(shopId), 'activities', limit],
    enabled: !!shopId && enabled,
    queryFn: async () => {
      const response = await HttpClient.get<{ data: ActivityLogDto[] }>(
        `${API_BASE}/${shopId}/activities?limit=${limit}`,
      );
      return response.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

export const useShopDashboardRecentSells = (shopId: string, enabled = true) =>
  useQuery({
    queryKey: [...shopDashboardKey(shopId), 'recent-sells'],
    enabled: !!shopId && enabled,
    queryFn: async () => {
      const response = await HttpClient.get<{ data: RecentSellInvoiceDto[] }>(
        `${API_BASE}/${shopId}/recent/sells?limit=10`,
      );
      return response.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

export const useShopDashboardRecentSellAdjustments = (shopId: string, enabled = true) =>
  useQuery({
    queryKey: [...shopDashboardKey(shopId), 'recent-sell-adjustments'],
    enabled: !!shopId && enabled,
    queryFn: async () => {
      const response = await HttpClient.get<{ data: RecentSellAdjustmentDto[] }>(
        `${API_BASE}/${shopId}/recent/sell-adjustments?limit=10`,
      );
      return response.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

export const useShopDashboardRecentStock = (shopId: string, enabled = true) =>
  useQuery({
    queryKey: [...shopDashboardKey(shopId), 'recent-stock'],
    enabled: !!shopId && enabled,
    queryFn: async () => {
      const response = await HttpClient.get<{ data: RecentStockMovementDto[] }>(
        `${API_BASE}/${shopId}/recent/stock-movements?limit=10`,
      );
      return response.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

export const useShopDashboardRecentLedgers = (shopId: string, enabled = true) =>
  useQuery({
    queryKey: [...shopDashboardKey(shopId), 'recent-ledgers'],
    enabled: !!shopId && enabled,
    queryFn: async () => {
      const response = await HttpClient.get<{ data: RecentLedgerEntryDto[] }>(
        `${API_BASE}/${shopId}/recent/ledgers?limit=10`,
      );
      return response.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });
